import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { retryOne, retryRun, updateNotes } from "../services/api";
import VersionDialog from "./VersionDialog";

const MAX_SNIP = 200;
const snip = (s, n = MAX_SNIP) => {
  const t = String(s || "");
  return t.length > n ? `${t.slice(0, n)}…` : t;
};

const formatDateTime = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  const time = d.toLocaleTimeString();
  return `${day}/${month}/${year}, ${time}`;
};

const REASONING_MODELS = new Set(["o4-mini", "gpt-5", "gpt-5-mini", "sonar-reasoning"]);

// ---------------------------------------------------------------------------
// Model family labeling (UI only)
// ---------------------------------------------------------------------------
const MODEL_FAMILY = {
  // OpenAI / ChatGPT family
  "gpt-4o": "ChatGPT",
  "gpt-4o-mini": "ChatGPT",
  "gpt-4.1-mini": "ChatGPT",
  "o4-mini": "ChatGPT",
  "gpt-5": "ChatGPT",
  "gpt-5-mini": "ChatGPT",

  // Perplexity
  "sonar": "Perplexity",
  "sonar-pro": "Perplexity",
  "sonar-reasoning": "Perplexity",

  // Gemini
  "gemini-2.5-flash": "Gemini",
  "gemini-2.5-flash-lite": "Gemini",
  "gemini-2.5-pro": "Gemini",

  // Claude (Anthropic)
  "claude-sonnet-4-6": "Claude",
  "claude-opus-4-6": "Claude",
  "claude-haiku-4-5": "Claude",

  // Google AI Overview via SearchAPI
  "searchapi-ai-overview": "AI Overview",
};

function formatModelDisplay(modelRaw) {
  const model = String(modelRaw || "").trim();
  if (!model) return "—";

  const key = model.toLowerCase();
  const fam = MODEL_FAMILY[key] || "Model";
  return `${fam} (${model})`;
}

function normalizeForExport(text) {
  if (text == null) return "";
  let s = String(text);
  s = s.replace(/\r\n/g, "\n");
  s = s.replace(/^\s{0,3}#{1,6}\s*/gm, "");
  s = s.replace(/^\s*([-*+]|\d{1,3}[.)])\s+/gm, "");
  s = s.replace(/\*\*/g, "").replace(/__/g, "").replace(/`/g, "");
  s = s.replace(/[ \t]+$/gm, "");
  return s.trim();
}

const csvQuote = (s) => `"${String(s ?? "").replace(/"/g, '""')}"`;

export default function AuditMatrix({ audit, matrix, onRefresh }) {
  const columns = useMemo(() => (Array.isArray(matrix?.columns) ? matrix.columns : []), [matrix?.columns]);
  const rows = useMemo(() => (Array.isArray(matrix?.rows) ? matrix.rows : []), [matrix?.rows]);

  const [busyCell, setBusyCell] = useState(null);
  const [runningRuns, setRunningRuns] = useState(new Set());
  const [fullscreen, setFullscreen] = useState(false);

  const [verModal, setVerModal] = useState({ open: false, aqId: null, selectedText: "" });
  const [notesModal, setNotesModal] = useState({ open: false, aqId: null, val: "" });

  const openNotes = (aqId, initial) => setNotesModal({ open: true, aqId, val: initial || "" });
  const closeNotes = () => setNotesModal({ open: false, aqId: null, val: "" });

  const saveNotes = async () => {
    if (!audit?.id || !notesModal.aqId) return;
    try {
      await updateNotes(audit.id, notesModal.aqId, notesModal.val);
      onRefresh?.();
    } catch (e) {
      console.error(e);
    } finally {
      closeNotes();
    }
  };

  const clearNotes = async (aqId) => {
    if (!audit?.id || !aqId) return;
    if (!window.confirm("Clear this note?")) return;
    try {
      await updateNotes(audit.id, aqId, "");
      onRefresh?.();
    } catch (e) {
      console.error(e);
    }
  };

  const [colWidths, setColWidths] = useState(() => Object.fromEntries(columns.map((c) => [c.run_id, 320])));

  useEffect(() => {
    if (columns.length === 0) return;
    setColWidths((prev) => {
      const out = { ...prev };
      for (const c of columns) if (out[c.run_id] == null) out[c.run_id] = 320;
      Object.keys(out).forEach((k) => {
        if (!columns.find((c) => String(c.run_id) === String(k))) delete out[k];
      });
      return out;
    });
  }, [columns]);

  const resizingRef = useRef({ runId: null, startX: 0, startW: 0 });

  const onStartResize = (runId, e) => {
    e.preventDefault();
    resizingRef.current = { runId, startX: e.clientX, startW: colWidths[runId] ?? 320 };
    document.addEventListener("mousemove", onResizeMove);
    document.addEventListener("mouseup", onResizeEnd);
  };

  const onResizeMove = (e) => {
    const { runId, startX, startW } = resizingRef.current;
    if (!runId) return;
    const dx = e.clientX - startX;
    const next = Math.max(180, Math.min(1600, startW + dx));
    setColWidths((prev) => ({ ...prev, [runId]: next }));
  };

  const onResizeEnd = () => {
    resizingRef.current = { runId: null, startX: 0, startW: 0 };
    document.removeEventListener("mousemove", onResizeMove);
    document.removeEventListener("mouseup", onResizeEnd);
  };

  const onRetryCell = async (runId, aqId) => {
    if (!runId || !aqId) return;
    if (!window.confirm("Retry this single question for this model?")) return;

    const k = `${runId}-${aqId}`;
    setBusyCell(k);
    try {
      await retryOne(runId, aqId);
      onRefresh?.();
    } catch (e) {
      console.error(e);
    } finally {
      setBusyCell(null);
    }
  };

  const onRetryRun = async (runId) => {
    if (!runId) return;
    if (!window.confirm("Retry the entire run for this model?")) return;

    setRunningRuns((s) => new Set(s).add(runId));
    try {
      await retryRun(runId);
      onRefresh?.();
    } catch (e) {
      console.error(e);
    } finally {
      setRunningRuns((s) => {
        const n = new Set(s);
        n.delete(runId);
        return n;
      });
    }
  };

  const exportVisibleCsv = () => {
    // ✅ quote headers too (safe if commas ever appear)
    const hdrs = [
      csvQuote("Question"),
      ...columns.map((c) => csvQuote(formatModelDisplay(c?.model))),
      csvQuote("Notes"),
    ];
    const lines = [hdrs.join(",")];

    for (const r of rows) {
      const qClean = normalizeForExport(r?.selected_text || "");
      const notesClean = normalizeForExport(r?.notes || "");

      const answers = columns.map((c) => {
        const cell = r?.cells?.[c.run_id];
        const raw = cell?.answer_text || cell?.error_text || "";
        const cleaned = normalizeForExport(raw);
        return csvQuote(cleaned);
      });

      lines.push([csvQuote(qClean), ...answers, csvQuote(notesClean)].join(","));
    }

    const csv = lines.join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const stamp = new Date().toISOString().slice(0, 16).replace(/[T:]/g, "-");
    const prefix = audit?.id ? `audit-${audit.id}_` : "";
    a.download = `${prefix}${stamp}.csv`;
    a.href = url;
    a.click();
    URL.revokeObjectURL(url);
  };

  const buildExportJson = () => ({
    audit_id: audit?.id,
    models: columns.map((c) => ({
      run_id: c.run_id,
      model: c.model,
      model_display: formatModelDisplay(c.model),
      created_at: c.created_at,
    })),
    rows: rows.map((r) => ({
      audit_question_id: r.audit_question_id,
      question_text_raw: r?.selected_text || "",
      question_text_plain: normalizeForExport(r?.selected_text || ""),
      notes_raw: r?.notes || "",
      notes_plain: normalizeForExport(r?.notes || ""),
      cells: Object.fromEntries(
        columns.map((c) => {
          const cell = r?.cells?.[c.run_id];
          if (!cell) return [c.run_id, null];
          const rawAns = cell.answer_text || "";
          const rawErr = cell.error_text || "";
          return [
            c.run_id,
            {
              model: c.model,
              model_display: formatModelDisplay(c.model),
              answer_raw: rawAns,
              answer_plain: normalizeForExport(rawAns),
              error_raw: rawErr,
              error_plain: normalizeForExport(rawErr),
              created_at: cell.created_at || null,
              cost_usd: cell.cost_usd ?? null,
              prompt_tokens: cell.prompt_tokens,
              completion_tokens: cell.completion_tokens,
              total_tokens: cell.total_tokens,
              stale: !!cell.stale,
            },
          ];
        })
      ),
    })),
  });

  const exportVisibleJson = () => {
    const payload = buildExportJson();
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json;charset=utf-8" });

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const stamp = new Date().toISOString().slice(0, 16).replace(/[T:]/g, "-");
    const prefix = audit?.id ? `audit-${audit.id}_` : "";
    a.download = `${prefix}${stamp}.json`;
    a.href = url;
    a.click();
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    if (!fullscreen) {
      document.body.style.overflow = "";
      return;
    }
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [fullscreen]);

  useEffect(() => {
    if (!audit && fullscreen) setFullscreen(false);
  }, [audit, fullscreen]);

  if (!audit) {
    return (
      <div style={{ marginTop: 12 }}>
        <div style={{ height: 18, width: 260, background: "rgba(15,23,42,0.06)", borderRadius: 10, marginBottom: 10 }} />
        <div style={{ height: 12, width: 360, background: "rgba(15,23,42,0.05)", borderRadius: 10, marginBottom: 18 }} />
        <div style={{ height: 240, width: "100%", background: "#fff", border: "1px solid rgba(226,232,240,0.95)", borderRadius: 14, boxShadow: "0 12px 28px rgba(15,23,42,0.08)" }} />
      </div>
    );
  }

  return (
    <div className={fullscreen ? "audit-matrix-fullscreen" : ""}>
      <div style={{ marginTop: fullscreen ? 0 : 12 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
          <div style={{ minWidth: 260 }}>
            <div style={{ fontWeight: 950, color: "#0f172a" }}>
              Audit #{audit.seq_no ?? audit.id}
              {audit.title ? ` — ${audit.title}` : ""}
            </div>
            <div style={{ color: "#64748b", fontSize: 12, fontWeight: 450 }}>
              Models: {columns.map((c) => formatModelDisplay(c?.model)).filter(Boolean).join(", ") || "—"} • Created: {formatDateTime(audit.created_at)}
            </div>
            {(() => {
              const locRun = columns.find((c) => c?.use_location && c?.location_country);
              if (!locRun) return null;
              const loc = [locRun.location_city, locRun.location_country].filter(Boolean).join(", ");
              return (
                <div style={{ color: "#64748b", fontSize: 12, fontWeight: 450 }}>
                  Location: <strong style={{ color: "#0f172a" }}>{loc}</strong>
                </div>
              );
            })()}
            {rows.length > 0 && columns.length > 0 && (
              <div style={{ color: "#64748b", fontSize: 12, fontWeight: 450 }}>
                Coverage: {columns.map((c) => {
                  const rid = c.run_id;
                  const total = rows.length;
                  const answered = rows.filter((r) => {
                    const cell = (r.cells || {})[rid];
                    return cell && (cell.answer_text || "").trim() && !(cell.error_text || "").trim();
                  }).length;
                  const errored = rows.filter((r) => {
                    const cell = (r.cells || {})[rid];
                    return cell && (cell.error_text || "").trim();
                  }).length;
                  const pct = total ? Math.round(100 * answered / total) : 0;
                  const color = pct === 100 ? "#059669" : pct >= 80 ? "#d97706" : "#ef4444";
                  return (
                    <span key={rid} style={{ marginRight: 12 }}>
                      {formatModelDisplay(c.model).split(" (")[0]}{" "}
                      <strong style={{ color }}>{answered}/{total}</strong>
                      {errored > 0 && <span style={{ color: "#ef4444", fontSize: 11 }}> ({errored} err)</span>}
                    </span>
                  );
                })}
              </div>
            )}
          </div>

          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button className="btn-secondary" onClick={exportVisibleCsv} style={{ borderRadius: 12 }}>
              Export CSV
            </button>
            <button className="btn-secondary" onClick={exportVisibleJson} style={{ borderRadius: 12 }}>
              Export JSON
            </button>
            <button className="btn-secondary" onClick={() => setFullscreen((f) => !f)} style={{ borderRadius: 12 }}>
              {fullscreen ? "Exit full screen" : "Full screen"}
            </button>
            <button
              className="btn-secondary"
              style={{ borderRadius: 12 }}
              onClick={async () => {
                try {
                  const { duplicateAudit, startRun } = await import("../services/api");
                  const res = await duplicateAudit(audit.id);
                  const msg = res.skipped_models?.length
                    ? `Audit duplicated (${res.skipped_models.join(", ")} no longer supported). `
                    : "Audit duplicated. ";
                  const doStart = window.confirm(msg + `Start fetching answers for the new audit (#${res.audit_id}) now?`);
                  if (doStart && res.run_ids?.length) {
                    for (const rid of res.run_ids) {
                      startRun(rid).catch(() => {});
                    }
                  }
                  // Reload the page to show new audit
                  window.location.reload();
                } catch (e) {
                  alert(e?.message || "Duplicate failed");
                }
              }}
            >
              Duplicate
            </button>
          </div>
        </div>

        <div style={{ overflowX: "auto", borderRadius: 14, border: "1px solid rgba(226,232,240,0.95)", boxShadow: "0 12px 28px rgba(15,23,42,0.08)", background: "#fff" }}>
          <table style={{ borderCollapse: "collapse", width: "100%", tableLayout: "fixed" }}>
            <colgroup>
              <col style={{ width: 420 }} />
              {columns.map((c) => (
                <col key={`col-${c.run_id}`} style={{ width: colWidths[c.run_id] ?? 320 }} />
              ))}
              <col style={{ width: 320 }} />
            </colgroup>

            <thead>
              <tr>
                <th style={{ ...thBrand, position: "sticky", top: 0, zIndex: 2 }}>Question (selected version)</th>

                {columns.map((c) => {
                  const modelName = c?.model || "";
                  const isReasoning = REASONING_MODELS.has(String(modelName).toLowerCase());
                  const modelDisplay = formatModelDisplay(modelName);

                  return (
                    <th key={c.run_id} style={{ ...thBrand, position: "sticky", top: 0, zIndex: 2 }}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {modelDisplay}
                          </span>
                          {isReasoning && (
                            <span
                              style={{
                                fontSize: 10,
                                padding: "2px 8px",
                                borderRadius: 999,
                                border: "1px solid rgba(0,0,0,0.9)",
                                background: "#ffcc33",
                                color: "#000",
                                textTransform: "uppercase",
                                letterSpacing: "0.06em",
                                fontWeight: 950,
                              }}
                            >
                              Reasoning
                            </span>
                          )}
                        </div>

                        <button
                          className="btn-tertiary"
                          onClick={() => onRetryRun(c.run_id)}
                          disabled={runningRuns.has(c.run_id)}
                          title="Retry entire run for this model"
                          style={{
                            background: "#ffcc33",
                            color: "#000",
                            border: "1px solid #000",
                            fontWeight: 950,
                            borderRadius: 12,
                            padding: "8px 12px",
                          }}
                        >
                          {runningRuns.has(c.run_id) ? "Running…" : "Run"}
                        </button>
                      </div>

                      <div
                        onMouseDown={(e) => onStartResize(c.run_id, e)}
                        style={{ position: "absolute", right: 0, top: 0, width: 10, height: "100%", cursor: "col-resize" }}
                        title="Drag to resize"
                      />
                    </th>
                  );
                })}

                <th style={{ ...thBrand, position: "sticky", top: 0, zIndex: 2 }}>Notes</th>
              </tr>
            </thead>

            <tbody>
              {rows.map((r, idxRow) => (
                <tr key={r?.audit_question_id ?? `row-${idxRow}`} style={{ background: idxRow % 2 === 0 ? "#fff" : "rgba(15,23,42,0.02)" }}>
                  <td style={td}>
                    <div style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere", wordBreak: "break-word", fontWeight: 650, color: "#0f172a" }}>
                      {snip(r?.selected_text, 400)}
                    </div>

                    <div style={{ marginTop: 8 }}>
                      <button
                        className="btn-secondary"
                        onClick={() =>
                          r?.audit_question_id &&
                          setVerModal({
                            open: true,
                            aqId: r.audit_question_id,
                            selectedText: r?.selected_text || "",
                          })
                        }
                        disabled={!r?.audit_question_id}
                        style={{ borderRadius: 12 }}
                      >
                        Versions…
                      </button>
                    </div>
                  </td>

                  {columns.map((c) => {
                    const runKey = c?.run_id;
                    const key = `${runKey}-${r?.audit_question_id ?? "na"}`;
                    const cell = r?.cells?.[runKey];
                    const isErr = cell?.error_text && !cell?.answer_text;

                    const snapshot = cell?.question_text_snapshot ?? null;
                    const modifiedSinceRun = snapshot && (r?.selected_text || "") !== (snapshot || "");

                    const hasTokenInfo =
                      cell &&
                      (cell.prompt_tokens != null ||
                        cell.completion_tokens != null ||
                        cell.total_tokens != null ||
                        typeof cell.cost_usd === "number");

                    const rawText = cell?.answer_text || cell?.error_text || "";
                    const costHigh = typeof cell?.cost_usd === "number" && Number(cell.cost_usd) >= 1;

                    return (
                      <td key={key} style={{ ...td, verticalAlign: "top" }}>
                        {modifiedSinceRun && (
                          <div
                            style={{
                              fontSize: 11,
                              fontWeight: 950,
                              padding: "3px 8px",
                              borderRadius: 999,
                              display: "inline-block",
                              marginBottom: 8,
                              background: "rgba(255,204,51,.16)",
                              color: "#a16207",
                              border: "1px solid rgba(161,98,7,.22)",
                            }}
                            title={`Was: "${(snapshot || "").substring(0, 200)}"\n\nNow: "${(r?.selected_text || "").substring(0, 200)}"`}
                          >
                            Question changed since this answer
                          </div>
                        )}

                        {/* Quality flags: language mismatch + answer length */}
                        {cell && !isErr && (() => {
                          const flags = [];
                          // G9: Language mismatch
                          const qLang = cell.question_language;
                          const aLang = cell.answer_language;
                          if (qLang && aLang && qLang !== aLang) {
                            flags.push(
                              <span key="lang" style={{ fontSize: 10, fontWeight: 800, padding: "2px 6px", borderRadius: 4, background: "#fef3c7", color: "#92400e", border: "1px solid #fde68a" }}
                                title={`Question language: ${qLang}, answer language: ${aLang}. The model responded in a different language than the question was asked in.`}>
                                {aLang.toUpperCase()}
                              </span>
                            );
                          }
                          // G10: Answer too short
                          const ansLen = (cell.answer_text || "").length;
                          if (ansLen > 0 && ansLen < 100) {
                            flags.push(
                              <span key="short" style={{ fontSize: 10, fontWeight: 800, padding: "2px 6px", borderRadius: 4, background: "#fff7ed", color: "#c2410c", border: "1px solid #fed7aa" }}
                                title="This answer is unusually short — it may be a refusal or incomplete response.">
                                SHORT
                              </span>
                            );
                          }
                          // G10: Refusal patterns
                          const lower = (cell.answer_text || "").toLowerCase();
                          if (lower.includes("i cannot") || lower.includes("i don't have") || lower.includes("nu am informații") || lower.includes("nu pot")) {
                            flags.push(
                              <span key="refuse" style={{ fontSize: 10, fontWeight: 800, padding: "2px 6px", borderRadius: 4, background: "#fef2f2", color: "#dc2626", border: "1px solid #fecaca" }}
                                title="This answer may contain a refusal or disclaimer.">
                                REFUSED
                              </span>
                            );
                          }
                          if (!flags.length) return null;
                          return <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 6 }}>{flags}</div>;
                        })()}

                        {runningRuns.has(runKey) && (
                          <div style={{ fontSize: 12, color: "#64748b", marginBottom: 8, fontWeight: 800 }}>
                            Running…
                          </div>
                        )}

                        {cell ? (
                          <div>
                            <div
                              style={{
                                whiteSpace: "normal",
                                overflowWrap: "anywhere",
                                wordBreak: "break-word",
                                color: isErr ? "#b91c1c" : "#0f172a",
                                maxHeight: 260,
                                overflowY: "auto",
                                paddingRight: 6,
                                lineHeight: 1.45,
                              }}
                            >
                              <ReactMarkdown remarkPlugins={[remarkGfm]}>{rawText || ""}</ReactMarkdown>
                            </div>

                            <div style={{ fontSize: 12, color: "#64748b", marginTop: 8, fontWeight: 750 }}>
                              {cell.created_at ? formatDateTime(cell.created_at) : ""}
                            </div>

                            {hasTokenInfo && (
                              <div style={{ fontSize: 11, color: "#475569", marginTop: 6, fontWeight: 800 }}>
                                Tokens: {cell.prompt_tokens != null ? cell.prompt_tokens : "—"} in /{" "}
                                {cell.completion_tokens != null ? cell.completion_tokens : "—"} out
                                {cell.total_tokens != null && <> • total {cell.total_tokens}</>}
                                {typeof cell.cost_usd === "number" && (
                                  <>
                                    {" "}
                                    • cost{" "}
                                    <span
                                      style={{
                                        color: costHigh ? "#b91c1c" : "inherit",
                                        fontWeight: costHigh ? 950 : 800,
                                      }}
                                      title={costHigh ? "High cost (≥ $1)" : undefined}
                                    >
                                      ${cell.cost_usd.toFixed(6)}
                                    </span>
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                        ) : (
                          <div style={{ color: "#94a3b8", fontWeight: 800 }}>
                            {runningRuns.has(runKey) ? "…" : "No attempt"}
                          </div>
                        )}

                        <div style={{ marginTop: 10, display: "flex", gap: 8, flexWrap: "wrap" }}>
                          <button
                            className="btn-secondary"
                            onClick={() => r?.audit_question_id && onRetryCell(runKey, r.audit_question_id)}
                            disabled={busyCell === key || !r?.audit_question_id}
                            style={{ borderRadius: 12 }}
                          >
                            {busyCell === key ? "Retrying…" : "Retry"}
                          </button>
                        </div>
                      </td>
                    );
                  })}

                  <td style={td}>
                    <div style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere", wordBreak: "break-word", maxHeight: 160, overflowY: "auto" }}>
                      {r?.notes ? snip(r.notes, 300) : <span style={{ color: "#94a3b8", fontWeight: 800 }}>No notes</span>}
                    </div>

                    <div style={{ marginTop: 10, display: "flex", gap: 8, flexWrap: "wrap" }}>
                      <button
                        className="btn-tertiary"
                        onClick={() => openNotes(r?.audit_question_id, r?.notes)}
                        style={{
                          background: "#ffcc33",
                          color: "#000",
                          border: "1px solid #000",
                          fontWeight: 950,
                          borderRadius: 12,
                        }}
                      >
                        Edit note…
                      </button>

                      {r?.notes ? (
                        <button
                          className="btn-tertiary"
                          onClick={() => clearNotes(r?.audit_question_id)}
                          style={{
                            background: "#ffcc33",
                            color: "#000",
                            border: "1px solid #000",
                            fontWeight: 950,
                            borderRadius: 12,
                          }}
                        >
                          Clear note
                        </button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {verModal.open && audit?.id ? (
          <VersionDialog
            open={verModal.open}
            auditId={audit.id}
            aqId={verModal.aqId}
            selectedText={verModal.selectedText}
            onClose={() => setVerModal({ open: false, aqId: null, selectedText: "" })}
            onSelected={onRefresh}
          />
        ) : null}

        {notesModal.open && (
          <div className="modal-backdrop" role="dialog" aria-modal="true">
            <div className="modal-card" style={{ width: 720, maxWidth: "100%", borderRadius: 18 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                <h3 style={{ margin: 0, color: "#0f172a", fontWeight: 950 }}>Edit note</h3>
                <button className="btn-secondary" onClick={closeNotes} style={{ borderRadius: 12 }}>
                  ✕
                </button>
              </div>
              <textarea
                rows={14}
                className="fld"
                value={notesModal.val}
                onChange={(e) => setNotesModal((s) => ({ ...s, val: e.target.value }))}
                placeholder="Write your note…"
                style={{ width: "100%" }}
              />
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 12 }}>
                <button className="btn-secondary" onClick={closeNotes} style={{ borderRadius: 12 }}>
                  Cancel
                </button>
                <button className="btn-primary" onClick={saveNotes} style={{ borderRadius: 12, fontWeight: 950 }}>
                  Save
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}

const thBrand = {
  border: "1px solid #000000",
  padding: "10px 8px",
  textAlign: "left",
  color: "#ffffff",
  background: "#000000",
  position: "relative",
};

const td = {
  border: "1px solid rgba(226,232,240,0.95)",
  padding: "10px 10px",
  verticalAlign: "top",
};
